import React, { useId } from 'react';

import TextField from '@rover/components/src/formFields/TextField';
import Calendar from '@rover/icons/svg/calendar.svg';
import { Box } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';

export type InlineErrorDateInputProps = {
  onKeyDown?: (any) => void;
  value: string;
  inputId?: string;
  errorMessage: errorMessageType;
  placeholder?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
  onIconClick?: () => void;
  disabled?: boolean;
  ariaHidden?: boolean;
  tabIndex?: number;
  className?: string;
};

// errorMessageType needs to accept all translation formats, i.e. t`, <Trans> and i18n strings
export type errorMessageType = JSX.Element | string | null | undefined;

const InlineErrorDateInput = ({
  errorMessage,
  onKeyDown,
  value,
  inputId,
  placeholder = '',
  onClick,
  style,
  icon,
  onIconClick,
  disabled = false,
  ariaHidden = false,
  className,
  ...other
}: InlineErrorDateInputProps): JSX.Element => {
  const { text, space, background, colors } = useTheme();
  const generatedInputId = useId();
  const textFieldStyle: React.CSSProperties = {
    fontSize: isMobileBrowser() ? text['100'].fontSize : text['200'].fontSize,
    cursor: 'pointer',
    minHeight: space['12x'],
    // input type button overrides
    textAlign: 'left',
    backgroundColor: background.primary,
    lineHeight: 0,
    ...style,
  };

  return (
    <Box position="relative">
      <TextField
        value={value}
        placeholder={placeholder}
        errorMessage={errorMessage as string}
        disabled={disabled}
        id={inputId ?? generatedInputId}
        inputProps={{
          onKeyDown,
          style: textFieldStyle,
          className,
          'aria-controls': other['aria-controls'],
          'aria-expanded': other['aria-expanded'],
          ...(other['aria-label']
            ? { 'aria-label': other['aria-label'], 'aria-labelledby': undefined }
            : { 'aria-labelledby': `${other['aria-labelledby'] ?? ''} ${generatedInputId}` }),
          'aria-hidden': ariaHidden,
          // Design has this input as a text field, but it's a button for the purposes of accessibility
          type: 'button',
          onClick,
          ...other,
        }}
        rightItem={
          value ? (
            icon // StyledCloseIcon
          ) : (
            <Calendar
              width="24px"
              height="24px"
              fill={colors.text.tertiary}
              data-testid="default-icon"
            />
          )
        }
        rightItemClick={onIconClick}
      />
    </Box>
  );
};

export default InlineErrorDateInput;
